import { useState, useEffect } from 'react';

/**
 * Demo data service - loads static snapshot of open houses
 * This bypasses all time-based filtering and API calls
 */

let demoData = null;
let isLoading = false;
let loadPromise = null;

/**
 * Transform API response to open house entries (same logic as openHouseDataService)
 */
const transformOpenHouseData = (rawData) => {
  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
    return [];
  }

  const openHouses = [];

  rawData.forEach((listing) => {
    if (!listing.OpenHouseStartTime) {
      return;
    }

    const photos = listing.Media || [];
    const photoUrls = photos
      .filter(media => media.MediaCategory === 'Photo')
      .sort((a, b) => (a.Order || 0) - (b.Order || 0))
      .map(media => media.MediaURL);

    const agentFirstName = listing.ListAgentFirstName || '';
    const agentLastName = listing.ListAgentLastName || '';
    const agentName = `${agentFirstName} ${agentLastName}`.trim();

    const startTime = new Date(listing.OpenHouseStartTime);
    const endTime = new Date(listing.OpenHouseEndTime);
    const id = `${listing.ListingKey}_${startTime.getTime()}`;

    openHouses.push({
      id,
      listingKey: listing.ListingKey,
      listingId: listing.ListingId || '',
      openHouseStartTime: startTime,
      openHouseEndTime: endTime,
      openHouseRemarks: listing.OpenHouseRemarks || '',
      address: listing.UnparsedAddress || '',
      price: listing.ListPrice || 0,
      beds: listing.BedroomsTotal || 0,
      baths: listing.BathroomsTotalInteger || 0,
      sqft: listing.LivingArea || 0,
      city: listing.City || '',
      state: listing.StateOrProvince || '',
      postalCode: listing.PostalCode || '',
      latitude: listing.Latitude || 0,
      longitude: listing.Longitude || 0,
      photo: photoUrls[0] || '',
      photos: photoUrls,
      agentFirstName,
      agentLastName,
      agentName,
      subdivisionName: listing.SubdivisionName || '',
      hostMemberKey: listing.HostMemberKey || null,
      hostDisplayName: listing.HostDisplayName || null,
      hostFirstName: listing.HostFirstName || null,
      hostLastName: listing.HostLastName || null,
      raw: listing
    });
  });

  // Sort by open house start time (earliest first), then by end time (earliest first) as secondary sort
  return openHouses.sort((a, b) => {
    const startTimeDiff = a.openHouseStartTime - b.openHouseStartTime;
    if (startTimeDiff !== 0) {
      return startTimeDiff;
    }
    // If start times are equal, sort by end time (earliest first)
    return a.openHouseEndTime - b.openHouseEndTime;
  });
};

/**
 * Load demo data from static JSON file with enhanced caching
 */
async function loadDemoData() {
  if (isLoading) {
    return loadPromise;
  }

  if (demoData) {
    return demoData;
  }

  isLoading = true;

  loadPromise = (async () => {
    try {
      console.log('📂 Loading demo data from static file...');
      
      // Use cache-first strategy with fallback
      const cacheKey = 'demo-data-v1';
      let response;
      
      // Try to get from cache first
      if ('caches' in window) {
        const cache = await caches.open('demo-cache');
        const cachedResponse = await cache.match('/demo-data.json');
        if (cachedResponse) {
          response = cachedResponse;
        }
      }
      
      // If not in cache, fetch from network
      if (!response) {
        response = await fetch('/demo-data.json', {
          cache: 'force-cache', // Use browser cache aggressively
          headers: {
            'Cache-Control': 'max-age=31536000', // 1 year
          }
        });
        
        // Store in cache for future use
        if ('caches' in window && response.ok) {
          const cache = await caches.open('demo-cache');
          cache.put('/demo-data.json', response.clone());
        }
      }
      
      if (!response.ok) {
        throw new Error(`Failed to load demo data: ${response.status}`);
      }

      const data = await response.json();
      const listings = data.value || [];
      
      // Transform the data (same logic as live data)
      const transformedData = transformOpenHouseData(listings);
      
      console.log(`✅ Loaded ${transformedData.length} demo open houses`);
      
      demoData = transformedData;
      return demoData;
    } catch (error) {
      console.error('❌ Failed to load demo data:', error);
      throw error;
    } finally {
      isLoading = false;
    }
  })();

  return loadPromise;
}

/**
 * React hook to use demo open houses data
 * Mimics the useOpenHouses hook but with static data
 */
export const useDemoOpenHouses = () => {
  const [openHouses, setOpenHouses] = useState(demoData || []);
  const [loading, setLoading] = useState(!demoData);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!demoData) {
      setLoading(true);
      loadDemoData()
        .then(() => {
          setOpenHouses(demoData);
          setLoading(false);
        })
        .catch(err => {
          setError(err);
          setLoading(false);
        });
    } else {
      setOpenHouses(demoData);
    }
  }, []);

  return { 
    openHouses,
    loading, 
    error 
  };
};

/**
 * Get demo open houses data (non-hook version)
 */
export const getDemoOpenHouses = async () => {
  if (!demoData) {
    await loadDemoData();
  }
  return demoData;
};
